import { preferencesKey, type Runtime } from "@baton/core";
import { type ThemeSetting, type ThemeState } from "@baton/ui";

/**
 * FR-015-130, `contracts/design-system.md` § Token architecture: light, dark and system,
 * switched from Preferences, the palette and `Cmd+Shift+L`, applied by `ThemeProvider`
 * rewriting the document's custom-property attributes with no flash, and persisted per
 * browser in the preferences envelope the sign-in screen reads before its first paint.
 * `PATCH /me` carries no theme member in the committed 001 contract (its writable set is
 * `display_name`, `avatar_key` and `timezone`), so the per-user half waits on that
 * contract; this module is the one place it will be added.
 */
export const THEME_ORDER: readonly ThemeSetting[] = ["light", "dark", "system"];

export function initialThemeState(runtime: Runtime): Partial<ThemeState> {
  const stored = runtime.storage.get(preferencesKey);
  if (stored === null) return {};
  return {
    ...(stored.theme === undefined ? {} : { theme: stored.theme }),
    ...(stored.density === undefined ? {} : { density: stored.density }),
    ...(stored.motion === undefined ? {} : { motion: stored.motion }),
  };
}

export function persistThemeState(runtime: Runtime, state: ThemeState): void {
  const stored = runtime.storage.get(preferencesKey) ?? {};
  runtime.storage.set(preferencesKey, { ...stored, theme: state.theme, density: state.density, motion: state.motion });
}

export function nextTheme(current: ThemeSetting): ThemeSetting {
  const index = THEME_ORDER.indexOf(current);
  return THEME_ORDER[(index + 1) % THEME_ORDER.length] ?? "system";
}
